import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {Optional} from 'util/Types';

import {BLUE} from './Colors';

export interface IndeterminateCheckboxProps<T> {
  className?: Optional<string>;
  disabled: boolean;
  items: Array<T>;
  /** A function to determine whether a checkable item is disabled */
  disabledMap?: (item: T) => boolean;
  /** A function to determine whether a checkable item is  checked */
  selectedMap: (item: T) => boolean;
  /** A function to be called when this checkbox is toggled */
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * A checkbox that can display indeterminate (partially checked) state. This component is intended to be used
 * at the top of a list of checkable items. It should render a regular checked state when all items are
 * checked, a regular unchecked state when all items are unchecked, and an indeterminate state when some items
 * are checked and some items are not.
 */
function IndeterminateCheckbox<T>(props: IndeterminateCheckboxProps<T>): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  // filter out disabled rows in order to enable checking all editable boxes
  const everySelected = props.items
    .filter((item) => props.disabledMap == null || !props.disabledMap(item))
    .every((item) => props.selectedMap(item));
  const someSelected = props.items
    .filter((item) => props.disabledMap == null || !props.disabledMap(item))
    .some((item) => props.selectedMap(item));
  const indeterminate = !everySelected && someSelected;

  useEffect(() => {
    if (ref.current != null) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      checked={someSelected}
      className={classNames(props.className, css(styles.checkbox))}
      disabled={props.disabled}
      type="checkbox"
      onChange={props.onChange}
      ref={ref}
      {...getTestProps({component: 'IndeterminateCheckbox', element: 'root'})}
    />
  );
}

IndeterminateCheckbox.defaultProps = {disabled: false};
IndeterminateCheckbox.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  disabledMap: PropTypes.func,
  selectedMap: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default IndeterminateCheckbox;
